import { createFeatureSelector, createSelector } from '@ngrx/store';
import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
import { FilterDef } from '@src/suite/common/data-models/navigation/filter-def';
import { ViewStateDef } from '@src/suite/common/data-models/navigation/viewstate-def';
import { ViewStateFilter } from '@src/suite/common/data-models/navigation/viewstate-filter';
import { ApFeedViewStateRecord, FtpFeedViewStateRecord } from '@src/suite/common/data-models/records/ap-feed-viewstate-record';
import { MultipleFeedViewStateRecord } from '@src/suite/common/data-models/records/mutiple-feed-viewstate-record';
import { PublicationChannelRecord } from '@src/suite/common/data-models/records/publication-channel-record';
import { PublicationViewStateRecord } from '@src/suite/common/data-models/records/publication-viewstate-record';
import { RSSFeedViewStateRecord } from '@src/suite/common/data-models/records/rss-feed-viewstate-record';
import { SingleViewStateRecord } from '@src/suite/common/data-models/records/single-viewstate-record';
import { TopicViewStateRecord } from '@src/suite/common/data-models/records/topic-viewstate-record';
import { TwitterFeedViewStateRecord } from '@src/suite/common/data-models/records/twitter-feed-viewstate-record';
import { ViewStateRecord } from '@src/suite/common/data-models/records/viewstate-record';
import { ViewStateSortOders } from '@src/suite/common/enums/viewstate-sort-orders';
import { GeneralViewStateTypes, ViewStateTypes } from '@src/suite/common/enums/viewstate-types';
import { selectPSTGroupPrivileges } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { selectPublicationChannelsMap } from '@src/suite/common/store/records/records.selectors';
import { getLayoutOptions } from '@src/suite/common/utils/viewstate-utils';

import { CreateViewStateState } from './createviewstate.models';

const selectCreateViewStateState = createFeatureSelector<CreateViewStateState>('createviewstate');

export const selectCreateViewStateData = createSelector(selectCreateViewStateState, state => state);
export const selectCreateViewStateGroup = createSelector(selectCreateViewStateData, (state: CreateViewStateState) => {
    return state.selectedGroup;
});
export const selectCreateViewStateSection = createSelector(selectCreateViewStateData, (state: CreateViewStateState) => {
    return state.selectedSection;
});
export const selectCreateViewStateItem = createSelector(selectCreateViewStateData, (state: CreateViewStateState) => {
    return state.selectedItem;
});
export const selectCreateViewIsLoading = createSelector(selectCreateViewStateData, (state: CreateViewStateState) => {
    return state.isLoading;
});
export const selectCreateViewInEditMode = createSelector(selectCreateViewStateData, (state: CreateViewStateState) => {
    return state.inEditMode;
});
export const selectCreateViewIsCopy = createSelector(selectCreateViewStateData, (state: CreateViewStateState) => {
    return state.isCopy;
});
export const selectCreateViewStateViewState = createSelector(selectCreateViewStateData, (state: CreateViewStateState) => {
    return state.viewState;
});
export const selectCreateViewStateTitle = createSelector(selectCreateViewStateData, (state: CreateViewStateState) => {
    return state.currentTitle;
});
export const selectCreateViewStateGeneralType = createSelector(selectCreateViewStateViewState, (vs: ViewStateRecord) => {
    if (vs) {
        if (vs.viewStateType === ViewStateTypes.TOPIC || vs.viewStateType === ViewStateTypes.ASSIGNMENT || vs.viewStateType === ViewStateTypes.SCRIPT || vs.viewStateType === ViewStateTypes.SEARCH)
            return GeneralViewStateTypes.SINGLE;
        if (
            vs.viewStateType === ViewStateTypes.RSSFEED ||
            vs.viewStateType === ViewStateTypes.TWITTERFEED ||
            vs.viewStateType === ViewStateTypes.WIREFEED ||
            vs.viewStateType === ViewStateTypes.EMAILFEED ||
            vs.viewStateType === ViewStateTypes.APFEED ||
            vs.viewStateType === ViewStateTypes.FTPFEED
        )
            return GeneralViewStateTypes.SINGLEFEED;
        if (vs.viewStateType === ViewStateTypes.MULTIPLEFEED) return GeneralViewStateTypes.MULTIPLEFEED;
    }
    return GeneralViewStateTypes.SINGLE;
});
export const selectCreateViewStateType = createSelector(selectCreateViewStateViewState, (vs: ViewStateRecord) => {
    return vs ? vs.viewStateType : null;
});
export const selectIsSaveButtonDisabled = createSelector(selectCreateViewStateData, selectCreateViewStateViewState, (state: CreateViewStateState, vs: ViewStateRecord) => {
    if (state.isLoading || !vs) return true;

    if (vs instanceof MultipleFeedViewStateRecord && state.inAddContentSourceMode && state.inBrowseMode) {
        if (!vs.viewStateSequence.Valid) return true;
        return false;
    }

    // Check if title is not empty
    let disabled: boolean = !vs.title || !vs.title.trim();

    if (vs instanceof PublicationViewStateRecord) {
        disabled = disabled || !vs.publicationChannelGUID || !(vs.viewStateShowTopics || vs.viewStateShowAssignments);
    }

    if (vs instanceof RSSFeedViewStateRecord) {
        disabled = disabled || !vs.viewStateFeedURL || !vs.viewStateFeedURL.trim();
    }

    if (vs instanceof ApFeedViewStateRecord) {
        disabled = disabled || !vs.viewStateFeedID;
    }

    if (vs instanceof TwitterFeedViewStateRecord) {
        disabled = disabled || !vs.viewStateFeedNames || vs.viewStateFeedNames.length === 0;
    }

    return disabled;
});
export const selectCreateViewStateGroupBySortIcon = createSelector(selectCreateViewStateViewState, (vs: ViewStateRecord) => {
    if (!vs) return null;
    const icon = (vs as SingleViewStateRecord)?.viewStateGroupSortOrder === ViewStateSortOders.ASC ? 'arrow_upward' : 'arrow_downward';
    return icon;
});
export const selectCurrentSortBySortIcon = createSelector(selectCreateViewStateViewState, (vs: ViewStateRecord) => {
    if (!vs) return null;
    const icon = (vs as SingleViewStateRecord)?.viewStateSortOrder === ViewStateSortOders.ASC ? 'arrow_upward' : 'arrow_downward';
    return icon;
});

export const selectCreateViewStateLayout = createSelector(selectCreateViewStateViewState, (vs: ViewStateRecord) => {
    return vs instanceof SingleViewStateRecord || vs instanceof PublicationViewStateRecord ? vs?.viewStateLayout : null;
});
export const selectCreateViewStateGroupBy = createSelector(selectCreateViewStateViewState, (vs: ViewStateRecord) => {
    return vs instanceof SingleViewStateRecord ? vs.viewStateGrouping : null;
});
export const selectCreateViewStateSortBy = createSelector(selectCreateViewStateViewState, (vs: ViewStateRecord) => {
    return vs instanceof SingleViewStateRecord ? vs.viewStateSorting : null;
});
export const selectCreateViewStateLockMapLocation = createSelector(selectCreateViewStateViewState, (vs: ViewStateRecord) => {
    return vs instanceof TopicViewStateRecord ? vs.viewStateMapLockMapLocation : null;
});
export const selectCreateViewStateMapShowTeamLocations = createSelector(selectCreateViewStateViewState, (vs: ViewStateRecord) => {
    return vs instanceof TopicViewStateRecord ? vs.viewStateMapShowTeamLocations : null;
});
export const selectCreateViewStateMapZoomToCoverage = createSelector(selectCreateViewStateViewState, (vs: ViewStateRecord) => {
    return vs instanceof TopicViewStateRecord ? vs.viewStateMapZoomToCoverage : null;
});
export const selectCreateViewStateFilter = createSelector(selectCreateViewStateViewState, (vs: ViewStateRecord) => {
    if (vs instanceof SingleViewStateRecord || vs instanceof PublicationViewStateRecord) {
        return vs.viewStateFilter;
    }
    return null;
});
export const selectCreateViewStateFilters = createSelector(selectCreateViewStateViewState, selectCreateViewStateFilter, (vs: ViewStateRecord, filter: ViewStateFilter) => {
    if (vs.viewStateType === ViewStateTypes.TOPIC || vs.viewStateType === ViewStateTypes.ASSIGNMENT || vs.viewStateType === ViewStateTypes.SCRIPT || vs.viewStateType === ViewStateTypes.ROLIST) {
        const vsRec = <TopicViewStateRecord>vs;

        if (vsRec && ((vsRec.viewStateLayout && vsRec.viewStateLayout.Value !== 'map') || vsRec.viewStateType === ViewStateTypes.ASSIGNMENT || vsRec.viewStateType === ViewStateTypes.SCRIPT)) {
            return filter.allFilters.filter(f => f.ID !== PSTFieldNames.PSTTeamLocation);
        }
        return filter.allFilters;
    }
    return filter.allFilters.filter(f => f.ID !== PSTFieldNames.PSTTeamLocation);
});
export const selectCreateViewStateAllFilters = createSelector(selectCreateViewStateFilter, (filter: ViewStateFilter) => {
    return filter ? filter.allFilters : null;
});
export const selectCreateViewStateRequiredFilters = createSelector(selectCreateViewStateFilter, (filter: ViewStateFilter) => {
    return filter ? filter.requiredFilters : null;
});
export const selectCreateViewStateHasRequiredFilters = createSelector(selectCreateViewStateFilter, (filter: ViewStateFilter) => {
    return filter && filter.requiredFilters && filter.requiredFilters.length > 0 ? true : false;
});
export const selectCreateViewStateRequiredFiltersIDs = createSelector(selectCreateViewStateRequiredFilters, (filters: Array<FilterDef>) => {
    return filters ? filters.map(item => item.ID) : null;
});
export const selectCreateViewStateOptionalFilters = createSelector(selectCreateViewStateFilter, (filter: ViewStateFilter) => {
    return filter ? filter.optionalFilters : null;
});
export const selectCreateViewStateHasOptionalFilters = createSelector(selectCreateViewStateFilter, (filter: ViewStateFilter) => {
    return filter && filter.optionalFilters && filter.optionalFilters.length > 0 ? true : false;
});
export const selectCreateViewStateOptionalFiltersIDs = createSelector(selectCreateViewStateOptionalFilters, (filters: Array<FilterDef>) => {
    return filters ? filters.map(item => item.ID) : null;
});
export const selectIsDiscoverViewState = createSelector(selectCreateViewStateViewState, (vs: ViewStateRecord) => {
    return vs instanceof RSSFeedViewStateRecord || vs instanceof TwitterFeedViewStateRecord || vs instanceof ApFeedViewStateRecord || vs instanceof MultipleFeedViewStateRecord ? true : false;
});
export const selectCreateViewStateShowTopics = createSelector(selectCreateViewStateViewState, (vs: ViewStateRecord) => {
    if (vs instanceof PublicationViewStateRecord) {
        return vs.viewStateShowTopics;
    }
    return null;
});
export const selectCreateViewStateShowAssignments = createSelector(selectCreateViewStateViewState, (vs: ViewStateRecord) => {
    if (vs instanceof PublicationViewStateRecord) {
        return vs.viewStateShowAssignments;
    }
    return null;
});
export const selectCreateViewStateShowStories = createSelector(selectCreateViewStateViewState, (vs: ViewStateRecord) => {
    if (vs instanceof PublicationViewStateRecord) {
        return vs.viewStateShowStories;
    }
    return null;
});
export const selectCreateViewStateRemoveAddedRecords = createSelector(selectCreateViewStateViewState, (vs: ViewStateRecord) => {
    if (vs instanceof PublicationViewStateRecord) {
        return vs.viewStateRemoveAddedRecords;
    }
    return null;
});
export const selectCreateViewStateViewStateGUID = createSelector(selectCreateViewStateViewState, (vs: ViewStateRecord) => {
    if (vs instanceof PublicationViewStateRecord) {
        return vs.viewStateGUID;
    }
    return null;
});
export const selectCreateViewStatePublicationChannelGUID = createSelector(selectCreateViewStateViewState, (vs: ViewStateRecord) => {
    if (vs instanceof PublicationViewStateRecord && vs.publicationChannelGUID) {
        return vs.publicationChannelGUID;
    }
    return null;
});
export const selectCreateViewStatePublicationChannel = createSelector(selectCreateViewStatePublicationChannelGUID, selectPublicationChannelsMap, (guid, publications) => {
    return guid ? publications?.get(guid) : null;
});
export const selectCreateViewStatePublicationChannelEditionsList = createSelector(selectCreateViewStatePublicationChannel, (publicationChannel: StorytellingPublicationChannelRecord) => {
    return publicationChannel?.PSTPublicationChannelEditionNames?.split('|');
});
export const selectCreateViewStateEditionName = createSelector(selectCreateViewStateViewState, (vs: ViewStateRecord) => {
    if (vs instanceof PublicationViewStateRecord) {
        return vs.editionName;
    }
    return null;
});
export const selectCreateViewStatePublicationListTitle = createSelector(selectCreateViewStateViewState, (vs: ViewStateRecord) => {
    if (vs instanceof PublicationViewStateRecord) {
        return vs.publicationListTitle;
    }
    return null;
});
// export const selectCreateViewStatePublicationChannelRecords = createSelector(() => {
//     // return DataStore.publicationChannelsResults.channels;

// });
export const selectCreateViewStatePlaybookViewStateRecords = createSelector(selectCreateViewStateState, state => {
    return state.storedViewStates || [];
});
export const selectCreateViewStateSelectedDefaultView = createSelector(
    selectCreateViewStatePlaybookViewStateRecords,
    selectCreateViewStateViewStateGUID,
    selectI18N,
    (records: Array<ViewStateRecord>, guid: string, i18n) => {
        const selectedView = records?.find(x => x.guid === guid);
        return selectedView?.title || ` ${i18n?.PSTWebDefaultViewLabel}`;
    }
);
export const selectCreateViewStateSelectedPublicationChannel = createSelector(selectCreateViewStatePublicationChannel, selectI18N, (selectedChannel, i18n) => {
    return selectedChannel?.Title || ` ${i18n?.PSTWebPublicationChannel}`;
});
export const selectCreateViewStateSelectedEditionName = createSelector(
    selectCreateViewStatePublicationChannel,
    selectCreateViewStateEditionName,
    selectI18N,
    (publicationChannel: PublicationChannelRecord, editionName: string, i18n) => {
        const selectedEditionName = publicationChannel?.editionsList?.find(x => x === editionName);
        return selectedEditionName || `${i18n?.PSTWebPublicationChannelEditionName}`;
    }
);
export const selectCreateViewStateFilterPublicationTime = createSelector(selectCreateViewStateFilter, (filter: ViewStateFilter) => {
    if (filter && filter.allFilters) return filter.allFilters.find(x => x.ID === PSTFieldNames.PSTPublicationTime);
    return null;
});
export const selectCreateViewStateFeedURL = createSelector(selectCreateViewStateViewState, (vs: ViewStateRecord) => {
    if (vs && vs instanceof RSSFeedViewStateRecord) {
        return vs.viewStateFeedURL;
    }
    return null;
});
export const selectCreateViewStateFeedID = createSelector(selectCreateViewStateViewState, (vs: ViewStateRecord) => {
    if (vs && (vs instanceof ApFeedViewStateRecord || vs instanceof FtpFeedViewStateRecord)) {
        return vs.viewStateFeedID;
    }
    return null;
});
export const selectCreateViewStateTwitterUsernames = createSelector(selectCreateViewStateViewState, (vs: ViewStateRecord) => {
    if (vs && vs instanceof TwitterFeedViewStateRecord) {
        return vs.viewStateFeedNames ? JSON.parse(vs.viewStateFeedNames) : [];
    }
    return null;
});
export const selectCreateIsInAddContentSourceMode = createSelector(selectCreateViewStateData, (state: CreateViewStateState) => {
    return state.inAddContentSourceMode;
});
export const selectCreateIsInConfigureMode = createSelector(selectCreateViewStateData, (state: CreateViewStateState) => {
    return state.inConfigureMode;
});
export const selectCreateIsInBrowseMode = createSelector(selectCreateViewStateData, (state: CreateViewStateState) => {
    return state.inBrowseMode;
});
export const selectCreateMultipleFeedViewStateMode = createSelector(selectCreateViewStateData, (state: CreateViewStateState) => {
    return state.multipleFeedViewStateMode;
});
export const selectCreateViewStateSequence = createSelector(selectCreateViewStateViewState, (vs: ViewStateRecord) => {
    if (vs instanceof MultipleFeedViewStateRecord) {
        return vs.viewStateSequence;
    }
    return null;
});
export const selectCreateViewStateSequenceAll = createSelector(selectCreateViewStateViewState, (vs: ViewStateRecord) => {
    if (vs instanceof MultipleFeedViewStateRecord) {
        return vs.viewStateSequence.viewstates;
    }
    return null;
});
export const selectFilterViewStateSequence = (value: string, types: Array<ViewStateTypes>) => {
    return createSelector(selectCreateViewStateSequenceAll, (viewstates: ViewStateDef[]) => {
        if (viewstates) {
            let res = viewstates.filter(item => item.Title.toLowerCase().includes(value.toLowerCase()));
            if (types && types.length > 0) res = res.filter(item => types.includes(item.Type));
            return res;
        }
        return null;
    });
};
export const selectCreateViewStateSequenceSelected = createSelector(selectCreateViewStateViewState, (vs: ViewStateRecord) => {
    if (vs instanceof MultipleFeedViewStateRecord) {
        return vs.viewStateSequence.viewstates.filter(item => item.Selected);
    }
    return null;
});
export const selectCreateViewStateLayoutOptions = createSelector(selectI18N, selectCreateViewStateViewState, selectPSTGroupPrivileges, (i18n, vs, PSTGroup) => {
    return getLayoutOptions(vs?.viewStateType, i18n, PSTGroup);
});
export const selectDiscoverCreateViewStateTypeOptions = createSelector(selectI18N, i18n => {
    return [
        { id: '' + ViewStateTypes.RSSFEED, title: i18n.PSTWebDiscoverRSSFeedLabel, selectable: true },
        { id: '' + ViewStateTypes.APFEED, title: i18n.PSTWebDiscoverApFeedLabel, selectable: true },
        { id: '' + ViewStateTypes.FTPFEED, title: i18n.PSTWebDiscoverFtpFeedLabel, selectable: true },
        { id: '' + ViewStateTypes.TWITTERFEED, title: i18n.PSTWebDiscoverTwitterFeedFeedLabel, selectable: true },
    ];
});
